import { FormErrorDisplay } from "@/components/form/form-error-display";
import TextFieldWithLabel from "@/components/form/hook-form/textfield-with-label";
import { FilledButton } from "@/components/ui/buttons/FilledButton";
import { AUTH_ACTIONS_LAYOUT_CLASS, AUTH_FORM_LAYOUT_CLASS } from "@/pages/auth-layout";
import { AUTH_CTA_CLASS, AUTH_LINK_CLASS, AuthScreenLayout } from "@/pages/auth-screen-layout";
import { APP_ROUTES } from "@/constants/app-routes";
import { useResetPassword } from "@/hooks/use-reset-password";
import {
  ResetPasswordEmailFormValues,
  ResetPasswordEmailSchema,
} from "@/schemas/reset-password-schema";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useIonRouter, useIonViewDidEnter } from "@ionic/react";
import { useRef } from "react";
import { useForm } from "react-hook-form";

export default function ResetPassword() {
  const navigation = useIonRouter();
  const { handleSendCode, isLoading } = useResetPassword();
  const emailInputRef = useRef<HTMLInputElement>(null);
  const initialValues: ResetPasswordEmailFormValues = {
    email: "",
  };

  useIonViewDidEnter(() => {
    emailInputRef.current?.focus();
  });

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors, isValid },
  } = useForm<ResetPasswordEmailFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ResetPasswordEmailSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  // Custom blur handler: only validate if field has content
  const handleEmailBlur = () => {
    const emailValue = getValues("email");
    if (emailValue && emailValue.trim() !== "") {
      trigger("email");
    }
  };

  return (
    <AuthScreenLayout
      title="Reset your password"
      subtitle="Enter your email to reset your password"
    >
      <form
        className={cn(AUTH_FORM_LAYOUT_CLASS, "justify-between")}
        onSubmit={handleSubmit((values) => handleSendCode(values.email))}
      >
        <div className="flex flex-col gap-8">
          <TextFieldWithLabel
            control={control}
            name="email"
            label="Email"
            placeholder="Please enter your email"
            type="email"
            autocomplete="email"
            ariaLabel="Email"
            id="email"
            inputClassName="w-full"
            error={errors.email?.message}
            showErrorMessage={false}
            scrollToOnFocus
            onBlur={handleEmailBlur}
            inputRef={emailInputRef}
            theme="dark"
          />
        </div>

        <div className={AUTH_ACTIONS_LAYOUT_CLASS}>
          <FilledButton
            disabled={!isValid || isLoading}
            text={isLoading ? "Sending..." : "Reset Password"}
            type="submit"
            color="white"
            textCase="normal"
            className={AUTH_CTA_CLASS}
          />

          <FormErrorDisplay formErrors={{}} apiError={null} fieldOrder={["email"]} />

          <button
            type="button"
            onClick={() => navigation.push(APP_ROUTES.REGISTER, "forward", "replace")}
            className={cn(
              textVariants.bodySmall,
              AUTH_LINK_CLASS,
              "mx-auto flex h-11 items-center justify-center gap-1 text-center font-medium",
            )}
          >
            <span>Don't have an account?</span>
            <span className="underline">Sign Up</span>
          </button>
        </div>
      </form>
    </AuthScreenLayout>
  );
}
